import React, { useState, useEffect } from 'react';
import { Trash2, Mail, MailOpen, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { contactMessagesAPI } from '../api';
import AdminLayout from '../components/AdminLayout';

const AdminContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await contactMessagesAPI.getAll();
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await contactMessagesAPI.markAsRead(messageId);
      setMessages(messages.map(msg => 
        msg._id === messageId ? { ...msg, is_read: true } : msg
      ));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleDelete = async (messageId) => {
    if (window.confirm('Bu mesajı silmek istediğinizden emin misiniz?')) {
      try {
        await contactMessagesAPI.delete(messageId);
        setMessages(messages.filter(msg => msg._id !== messageId));
        if (selectedMessage?._id === messageId) {
          setSelectedMessage(null);
        }
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = messages.filter(msg => !msg.is_read).length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <p className="text-gray-400">Yükleniyor...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">İletişim Mesajları</h1>
          <p className="text-gray-400 mt-1">
            Toplam {messages.length} mesaj • {unreadCount} okunmamış
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1 space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
          {messages.length === 0 ? (
            <Card className="bg-gray-900 border-gray-800 p-6 text-center">
              <p className="text-gray-400">Henüz mesaj yok</p>
            </Card>
          ) : (
            messages.map((message) => (
              <Card
                key={message._id}
                className={`bg-gray-900 border-gray-800 p-4 cursor-pointer transition-all hover:border-[#FDB714] ${
                  selectedMessage?._id === message._id ? 'border-[#FDB714]' : ''
                } ${!message.is_read ? 'border-l-4 border-l-blue-500' : ''}`}
                onClick={() => {
                  setSelectedMessage(message);
                  if (!message.is_read) {
                    handleMarkAsRead(message._id);
                  }
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {message.is_read ? (
                      <MailOpen size={16} className="text-gray-400" />
                    ) : (
                      <Mail size={16} className="text-blue-400" />
                    )}
                    <h3 className="font-semibold text-white text-sm">{message.name}</h3>
                  </div>
                </div>
                <p className="text-gray-400 text-xs mb-1">{message.email}</p>
                {message.subject && (
                  <span className="inline-block bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded mb-2">
                    {message.subject}
                  </span>
                )}
                <p className="text-gray-500 text-xs line-clamp-2">{message.message}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Calendar size={12} className="text-gray-500" />
                  <span className="text-gray-500 text-xs">{formatDate(message.created_at)}</span>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2">
          {selectedMessage ? (
            <Card className="bg-gray-900 border-gray-800 p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white mb-2">{selectedMessage.name}</h2>
                  <div className="space-y-1">
                    <a href={`mailto:${selectedMessage.email}`} className="text-[#FDB714] hover:underline block">
                      {selectedMessage.email}
                    </a>
                    {selectedMessage.phone && (
                      <a href={`tel:${selectedMessage.phone}`} className="text-gray-300 hover:text-[#FDB714] text-sm block">
                        📞 {selectedMessage.phone}
                      </a>
                    )}
                    {selectedMessage.subject && (
                      <div className="mt-2">
                        <span className="inline-block bg-blue-500/20 text-blue-400 text-xs px-3 py-1 rounded-full">
                          {selectedMessage.subject}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Calendar size={14} className="text-gray-400" />
                      <span className="text-gray-400 text-sm">{formatDate(selectedMessage.created_at)}</span>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => handleDelete(selectedMessage._id)}
                  variant="destructive"
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 size={16} className="mr-2" />
                  Sil
                </Button>
              </div>

              <div className="border-t border-gray-800 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-400 mb-2">Mesaj:</h3>
                <p className="text-white whitespace-pre-wrap leading-relaxed">
                  {selectedMessage.message}
                </p>
              </div>

              <div className="border-t border-gray-800 pt-4 mt-6">
                <Button
                  onClick={() => window.location.href = `mailto:${selectedMessage.email}?subject=Re: İletişim&body=Merhaba ${selectedMessage.name},%0D%0A%0D%0A`}
                  className="bg-[#FDB714] hover:bg-[#e5a510] text-black"
                >
                  <Mail size={16} className="mr-2" />
                  Yanıtla
                </Button>
              </div>
            </Card>
          ) : (
            <Card className="bg-gray-900 border-gray-800 p-12 text-center">
              <Mail size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Bir mesaj seçin</p>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContactMessages;
